import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';
import * as XLSX from 'xlsx';

const AdminTeacherManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for teachers data - CLIENT-SIDE FILTERING APPROACH
  const [allTeachers, setAllTeachers] = useState([]); // Store all teachers
  const [filteredTeachers, setFilteredTeachers] = useState([]); // Store filtered results
  const [displayedTeachers, setDisplayedTeachers] = useState([]); // Store paginated results
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllTeachers, setShowAllTeachers] = useState(false); // NEW: Track if showing all teachers

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(10);

  // FILTERING STATES
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  // DYNAMIC DEPARTMENT DATA
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    username: '',
    name: '',
    department: '',
    designation: '',
    email: '',
    phone_number: '',
    batch_name: '',
    blood_group: '',
    pan_number: '',
    adhaar_number: '',
    academic_qualification: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API HELPER FUNCTION
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // FETCH DEPARTMENTS FROM DATABASE
  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const result = await apiCall('/departments');

      if (result.success) {
        setDepartments(result.data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Failed to fetch departments: ' + error.message);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // FETCH INITIAL DATA
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Fetch ALL teachers from backend (CLIENT-SIDE FILTERING APPROACH)
  const fetchAllTeachers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch a large number of teachers
      const apiUrl = `${API_LINK}/api/admin/teachers?page=1&limit=10000`;

      // console.log('Fetching all teachers from API...');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Admin API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();
      // console.log('API Response:', result);

      if (result.success) {
        const teachersData = result.data?.teachers || [];

        // Store all teachers
        setAllTeachers(teachersData);

        // console.log(`Loaded ${teachersData.length} teachers from server`);

        // If showing all teachers, display them immediately
        if (showAllTeachers) {
          applyFilters(teachersData, searchTerm, filterDepartment, filterDesignation, true);
        } else {
          // Apply current filters if any exist
          const hasActiveFilters = searchTerm.trim() || filterDepartment || filterDesignation.trim();
          if (hasActiveFilters) {
            applyFilters(teachersData, searchTerm, filterDepartment, filterDesignation, false);
          } else {
            // Clear displayed data when no filters
            setFilteredTeachers([]);
            setDisplayedTeachers([]);
            setTotalCount(0);
            setTotalPages(0);
            setCurrentPage(1);
          }
        }

      } else {
        setError(result.message || 'Failed to fetch teachers from server');
      }
    } catch (error) {
      console.error('Fetch teachers error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load teachers: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Client-side filtering function with null checks
  const applyFilters = (teachers, searchText, department, designation, showAll = false) => {
    let filtered = [...teachers];

    // If showAll is true, skip filtering
    if (!showAll) {
      // Apply search filter (case-insensitive) with NULL CHECKS
      if (searchText && searchText.trim()) {
        const searchLower = searchText.trim().toLowerCase();
        filtered = filtered.filter(teacher => {
          // FIXED: Add null/undefined checks before calling toLowerCase()
          const name = teacher.name || '';
          const id = teacher.id || '';
          const email = teacher.email || '';
          const username = teacher.username || '';

          return name.toLowerCase().includes(searchLower) ||
            id.toLowerCase().includes(searchLower) ||
            email.toLowerCase().includes(searchLower) ||
            username.toLowerCase().includes(searchLower);
        });
      }

      // Apply department filter
      if (department && department.trim()) {
        filtered = filtered.filter(teacher =>
          teacher.department === department
        );
      }

      // Apply designation filter (case-insensitive) with NULL CHECK
      if (designation && designation.trim()) {
        const designationLower = designation.trim().toLowerCase();
        filtered = filtered.filter(teacher => {
          // FIXED: Add null/undefined check before calling toLowerCase()
          const teacherDesignation = teacher.designation || '';
          return teacherDesignation.toLowerCase().includes(designationLower);
        });
      }
    }

    // console.log(`Filtered ${filtered.length} teachers from ${teachers.length} total`);

    // Update filtered teachers
    setFilteredTeachers(filtered);
    setTotalCount(filtered.length);

    // Calculate pagination
    const totalPagesCalc = Math.ceil(filtered.length / limit);
    setTotalPages(totalPagesCalc);

    // Reset to first page when filters change
    setCurrentPage(1);

    // Apply pagination to filtered results
    applyPagination(filtered, 1);
  };

  // Client-side pagination function
  const applyPagination = (filteredData, page) => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setDisplayedTeachers(paginatedData);
    setCurrentPage(page);

    // console.log(`Showing page ${page}: ${paginatedData.length} teachers (${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length})`);
  };

  // Initial data fetch
  useEffect(() => {
    fetchAllTeachers();
  }, []);

  // NEW: Show all teachers function
  const handleShowAllTeachers = () => {
    setShowAllTeachers(true);
    setFilterDepartment('');
    setFilterDesignation('');
    setSearchTerm('');

    if (allTeachers.length > 0) {
      applyFilters(allTeachers, '', '', '', true); // Show all teachers
    }
  };

  // FILTER HANDLERS FOR CLIENT-SIDE FILTERING
  const handleFilterDepartmentChange = (value) => {
    setShowAllTeachers(false); // Disable show all when filtering
    setFilterDepartment(value);
  };

  const handleFilterDesignationChange = (value) => {
    setShowAllTeachers(false); // Disable show all when filtering
    setFilterDesignation(value);
  };

  const handleClearFilters = () => {
    setFilterDepartment('');
    setFilterDesignation('');
    setSearchTerm('');
    setShowAllTeachers(false);

    // Clear displayed data when filters are cleared
    setFilteredTeachers([]);
    setDisplayedTeachers([]);
    setTotalCount(0);
    setTotalPages(0);
    setCurrentPage(1);
  };

  // Handle search and filter changes - INSTANT FILTERING
  useEffect(() => {
    if (allTeachers.length > 0 && !showAllTeachers) {
      // Only apply filters if at least one filter is active
      const hasActiveFilters = searchTerm.trim() || filterDepartment || filterDesignation.trim();

      if (hasActiveFilters) {
        // console.log('Auto-applying filters:', { searchTerm, filterDepartment, filterDesignation });
        applyFilters(allTeachers, searchTerm, filterDepartment, filterDesignation, false);
      } else {
        // No active filters, clear display
        setFilteredTeachers([]);
        setDisplayedTeachers([]);
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filterDepartment, filterDesignation, allTeachers]);

  // Handle search term changes
  const handleSearchChange = (value) => {
    setShowAllTeachers(false); // Disable show all when searching
    setSearchTerm(value);
  };

  // Handle pagination changes
  const handlePageChange = (page) => {
    if (page && page >= 1 && page <= totalPages) {
      applyPagination(filteredTeachers, page);
    }
  };

  // Handle search manually (for search button)
  const handleSearch = () => {
    // console.log('Manual search triggered with term:', searchTerm);
    if (allTeachers.length > 0) {
      applyFilters(allTeachers, searchTerm, filterDepartment, filterDesignation, false);
    }
  };

  // Refresh teachers (re-fetch from server)
  const refreshTeachers = () => {
    fetchAllTeachers();
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.id.trim()) errors.id = 'Teacher ID is required';
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.department.trim()) errors.department = 'Department is required';
    if (!formData.designation.trim()) errors.designation = 'Designation is required';
    if (!formData.phone_number.trim()) errors.phone_number = 'Phone number is required';
    if (!formData.academic_qualification.trim()) errors.academic_qualification = 'Academic qualification is required';

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email format is invalid';
    }

    // Phone validation
    if (formData.phone_number && !/^\d{10}$/.test(formData.phone_number)) {
      errors.phone_number = 'Phone number must be 10 digits';
    }

    // Aadhaar validation
    if (formData.adhaar_number && !/^\d{12}$/.test(formData.adhaar_number)) {
      errors.adhaar_number = 'Aadhaar number must be 12 digits';
    }

    // PAN validation
    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
      errors.pan_number = 'PAN format should be like ABCDE1234F';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = selectedTeacher
        ? `${API_LINK}/api/admin/teachers/${selectedTeacher.id}`
        : `${API_LINK}/api/admin/teachers`;

      const method = selectedTeacher ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();

        // Refresh all teachers after successful add/edit
        await fetchAllTeachers();

        alert(selectedTeacher ? 'Teacher updated successfully!' : 'Teacher added successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      alert('Failed to save teacher. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedTeacher) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/teachers/${selectedTeacher.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedTeacher(null);

        // Refresh all teachers after successful delete
        await fetchAllTeachers();

        alert('Teacher deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert('Failed to delete teacher. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: '',
      username: '',
      name: '',
      department: '',
      designation: '',
      email: '',
      phone_number: '',
      batch_name: '',
      blood_group: '',
      pan_number: '',
      adhaar_number: '',
      academic_qualification: ''
    });
    setFormErrors({});
    setSelectedTeacher(null);
  };

  // Handle edit click
  const handleEditClick = (teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      id: teacher.id || '',
      username: teacher.username || '',
      name: teacher.name || '',
      department: teacher.department || '',
      designation: teacher.designation || '',
      email: teacher.email || '',
      phone_number: teacher.phone_number || '',
      batch_name: teacher.batch_name || '',
      blood_group: teacher.blood_group || '',
      pan_number: teacher.pan_number || '',
      adhaar_number: teacher.adhaar_number?.toString() || '',
      academic_qualification: teacher.academic_qualification || ''
    });
    setShowEditModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (teacher) => {
    setSelectedTeacher(teacher);
    setShowDeleteModal(true);
  };




  // Handle Excel export
  const handleExportExcel = () => {
    const dataToExport = filteredTeachers.length > 0 ? filteredTeachers : allTeachers;

    if (dataToExport.length === 0) {
      alert('No teacher data to export. Please load or filter teachers first.');
      return;
    }

    const excelData = dataToExport.map((teacher, index) => ({
      'S.No': index + 1,
      'Name': teacher.name || '',
      'Username': teacher.username || '',
      'Department': teacher.department || '',
      'Designation': teacher.designation || '',
      'Email': teacher.email || '',
      'Phone': teacher.phone_number || '',
      'Batch': teacher.batch_name || '',
      'Blood Group': teacher.blood_group || '',
      'PAN Number': teacher.pan_number || '',
      'Aadhaar Number': teacher.adhaar_number || '',
      'Academic Qualification': teacher.academic_qualification || ''
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const colWidths = Object.keys(excelData[0]).map(key => ({
      wch: Math.max(key.length, ...excelData.map(row => String(row[key] || '').length))
    }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Teachers');

    let filename = 'Teacher_Data';
    if (filterDepartment) filename += `_${filterDepartment}`;
    if (filterDesignation) filename += `_${filterDesignation}`;
    filename += `_${new Date().toISOString().split('T')[0]}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm.trim() || filterDepartment || filterDesignation.trim();

  return (
    <Layout role="admin" onNavigate={onNavigate} userInfo={userInfo} activePage="admin-teachers">
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Teacher Management Portal 👨‍🏫
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage all teacher records, add new teachers, and filter by department and designation.
        </p>
      </div>

      {/* FILTERS SECTION - INSTANT FILTERING */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>🔍</span>
          <h3 style={{ margin: 0, fontSize: '1.3rem', color: '#333' }}>
            Filter Teachers or View All
          </h3>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Department Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: '#374151',
              fontSize: '0.9rem'
            }}>
              Department
            </label>
            <select
              value={filterDepartment}
              onChange={(e) => handleFilterDepartmentChange(e.target.value)}
              disabled={departmentsLoading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.9rem',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                backgroundColor: departmentsLoading ? '#f9fafb' : '#ffffff',
                cursor: departmentsLoading ? 'not-allowed' : 'pointer'
              }}
              onFocus={(e) => e.target.style.borderColor = '#238548'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">
                {departmentsLoading ? 'Loading...' : 'Select Department'}
              </option>
              {departments.map(dept => (
                <option key={dept.value} value={dept.value}>
                  {dept.label}
                </option>
              ))}
            </select>
          </div>

          {/* Designation Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: '#374151',
              fontSize: '0.9rem'
            }}>
              Designation
            </label>
            <input
              type="text"
              value={filterDesignation}
              onChange={(e) => handleFilterDesignationChange(e.target.value)}
              placeholder="Search by designation..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.9rem',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                backgroundColor: '#ffffff'
              }}
              onFocus={(e) => e.target.style.borderColor = '#238548'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* NEW: Show All Teachers Button */}
          <button
            onClick={handleShowAllTeachers}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: showAllTeachers
                ? 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)'
                : 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            <span>👥</span>
            {showAllTeachers ? 'Showing All Teachers' : 'Show All Teachers'}
          </button>

          {/* Clear Filters Button */}
          <button
            onClick={handleClearFilters}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: '#6b7280',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.backgroundColor = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <span>🗑️</span>
            Clear All Filters
          </button>
        </div>

        {/* Active Filters Display */}
        {(hasActiveFilters || showAllTeachers) && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#0369a1'
            }}>
              <span>📌</span>
              {showAllTeachers ? 'Viewing All Teachers:' : 'Active Filters:'}
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              {showAllTeachers && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  All Teachers ({allTeachers.length})
                </span>
              )}
              {!showAllTeachers && searchTerm.trim() && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#7c3aed',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Search: {searchTerm.trim()}
                </span>
              )}
              {!showAllTeachers && filterDepartment && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#1e40af',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Dept: {departments.find(d => d.value === filterDepartment)?.label || filterDepartment}
                </span>
              )}
              {!showAllTeachers && filterDesignation.trim() && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: '#059669',
                  color: '#ffffff',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}>
                  Designation: {filterDesignation.trim()}
                </span>
              )}
            </div>
          </div>
        )}

        {/* No Filter Warning */}
        {!hasActiveFilters && !showAllTeachers && !loading && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
              Please select a department, search by designation, use the search bar, or click "Show All Teachers" to view data.
            </p>
          </div>
        )}
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Search Bar */}
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '400px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by name, teacher ID, or email..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  padding: '1rem 3rem 1rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={handleSearch}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.5rem'
                }}
              >
                🔍
              </button>
            </div>
          </div>

          {/* Add Teacher Button */}
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            Add New Teacher
          </button>

          {/* Refresh Button */}
          <button
            onClick={refreshTeachers}
            disabled={loading}
            style={{
              padding: '1rem',
              background: '#f0f9ff',
              color: '#0369a1',
              border: '2px solid #e0f2fe',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#0369a1';
                e.target.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#f0f9ff';
                e.target.style.color = '#0369a1';
              }
            }}
            title="Refresh teachers"
          >
            {loading ? (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #0369a1',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
              '🔄'
            )}
          </button>

          {/* Export Excel Button */}
          <button
            onClick={handleExportExcel}
            disabled={loading || (allTeachers.length === 0)}
            style={{
              padding: '1rem',
              background: '#f0fdf4',
              color: '#16a34a',
              border: '2px solid #bbf7d0',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: (loading || allTeachers.length === 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              opacity: (loading || allTeachers.length === 0) ? 0.6 : 1,
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              if (!loading && allTeachers.length > 0) {
                e.target.style.backgroundColor = '#16a34a';
                e.target.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && allTeachers.length > 0) {
                e.target.style.backgroundColor = '#f0fdf4';
                e.target.style.color = '#16a34a';
              }
            }}
            title={filteredTeachers.length > 0 ? `Export ${filteredTeachers.length} filtered teachers` : `Export all ${allTeachers.length} teachers`}
          >
            📥 Export Excel
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading && allTeachers.length === 0 ? 'Loading teachers...' :
              error ? 'Error loading teachers' :
                showAllTeachers ? `Showing all ${displayedTeachers.length} of ${allTeachers.length} teachers` :
                  hasActiveFilters ? `Showing ${displayedTeachers.length} of ${totalCount} teachers (filtered from ${allTeachers.length} total)` :
                    `${allTeachers.length} teachers loaded - Use filters or "Show All" to view data`}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Teachers</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={refreshTeachers}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Teachers Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && allTeachers.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading teachers from database...</p>
          </div>
        ) : displayedTeachers.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>👨‍🏫</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {hasActiveFilters || showAllTeachers ? 'No teachers found' : 'Ready to view teacher data'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {hasActiveFilters || showAllTeachers
                ? 'Try adjusting your search criteria or filters to find teachers'
                : 'Apply filters above or click "Show All Teachers" to view teacher records'
              }
            </p>
            {!hasActiveFilters && !showAllTeachers && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#e0f2fe',
                borderRadius: '8px',
                margin: '0 auto',
                maxWidth: '500px'
              }}>
                <p style={{ margin: 0, color: '#0369a1', fontSize: '0.9rem' }}>
                  <strong>📋 How to view teachers:</strong><br />
                  1. Click "Show All Teachers" to see everything<br />
                  2. OR Select a Department from the filter<br />
                  3. OR Search by Designation<br />
                  4. OR Use the Search box
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Teacher ID</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Name</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Department</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Designation</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Email</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Phone</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTeachers.map((teacher, index) => (
                    <tr key={teacher.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem', fontWeight: 500, color: '#238548' }}>
                        {teacher.id || 'N/A'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                            {teacher.name || 'No name'}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            @{teacher.username || 'no-username'}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'uppercase'
                        }}>
                          {teacher.department || 'No Dept'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {teacher.designation || 'No designation'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {teacher.email || 'Not provided'}
                      </td>
                      <td style={{ padding: '1rem', color: '#333' }}>
                        {teacher.phone_number || 'Not provided'}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEditClick(teacher)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#e3f2fd';
                              e.target.style.color = '#1976d2';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(teacher)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#ffebee',
                              color: '#d32f2f',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ffebee';
                              e.target.style.color = '#d32f2f';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total teachers
                  {showAllTeachers ? '' : `(filtered from ${allTeachers.length})`}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>


      {/* Add/Edit Modal */}
      {
        (showAddModal || showEditModal) && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '1.5rem 2rem',
                borderRadius: '12px 12px 0 0'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {selectedTeacher ? '✏️ Edit Teacher' : '➕ Add New Teacher'}
                </h3>
                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                  {selectedTeacher ? 'Update teacher information' : 'Enter new teacher details'}
                </p>
              </div>

              {/* Modal Content */}
              <div style={{
                padding: '2rem',
                overflowY: 'auto',
                flex: 1
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                  {/* Teacher ID */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Teacher ID *
                    </label>
                    <input
                      type="text"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                      placeholder="e.g., TCH-2025-001"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.id ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.id ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.id) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.id) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.id && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.id}
                      </p>
                    )}
                  </div>

                  {/* Username */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="Enter username"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.username ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.username ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.username) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.username) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.username && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.username}
                      </p>
                    )}
                  </div>

                  {/* Name */}
                  <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter teacher's full name"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.name ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.name ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.name) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.name) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.name && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  {/* Department */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Department *
                    </label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      disabled={departmentsLoading}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.department ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.department ? '#fef2f2' : '#ffffff',
                        cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.department) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.department) e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <option value="">
                        {departmentsLoading ? 'Loading departments...' : 'Select Department'}
                      </option>
                      {departments.map(dept => (
                        <option key={dept.value} value={dept.value}>{dept.label}</option>
                      ))}
                    </select>
                    {formErrors.department && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.department}
                      </p>
                    )}
                  </div>

                  {/* Designation */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Designation *
                    </label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      placeholder="e.g., Professor, Assistant Professor, Lecturer"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.designation ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.designation ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.designation) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.designation) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.designation && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.designation}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="teacher@example.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.email ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.email ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.email) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.email) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.email && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="10-digit phone number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.phone_number ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.phone_number ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.phone_number) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.phone_number) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.phone_number && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.phone_number}
                      </p>
                    )}
                  </div>

                  {/* Batch Name */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Batch Name
                    </label>
                    <input
                      type="text"
                      value={formData.batch_name}
                      onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                      placeholder="e.g., B.Tech-2025"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#238548'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  {/* Blood Group */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Blood Group
                    </label>
                    <select
                      value={formData.blood_group}
                      onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#238548'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>

                  {/* PAN Number */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      PAN Number
                    </label>
                    <input
                      type="text"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                      placeholder="ABCDE1234F"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.pan_number ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.pan_number ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.pan_number) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.pan_number) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.pan_number && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.pan_number}
                      </p>
                    )}
                  </div>

                  {/* Aadhaar Number */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Aadhaar Number
                    </label>
                    <input
                      type="text"
                      value={formData.adhaar_number}
                      onChange={(e) => setFormData({ ...formData, adhaar_number: e.target.value })}
                      placeholder="12-digit Aadhaar number"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.adhaar_number ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.adhaar_number ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.adhaar_number) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.adhaar_number) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.adhaar_number && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.adhaar_number}
                      </p>
                    )}
                  </div>

                  {/* Academic Qualification */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Academic Qualification *
                    </label>
                    <textarea
                      value={formData.academic_qualification}
                      onChange={(e) => setFormData({ ...formData, academic_qualification: e.target.value })}
                      placeholder="e.g., Ph.D. in Computer Science, M.Tech in Software Engineering"
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: `2px solid ${formErrors.academic_qualification ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.academic_qualification ? '#fef2f2' : '#ffffff',
                        resize: 'vertical',
                        minHeight: '80px'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.academic_qualification) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.academic_qualification) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    {formErrors.academic_qualification && (
                      <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                        {formErrors.academic_qualification}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                backgroundColor: '#f8fafc'
              }}>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 2rem',
                    border: 'none',
                    background: submitting
                      ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                      : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      {selectedTeacher ? '💾 Update Teacher' : '➕ Add Teacher'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && selectedTeacher && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '400px',
              width: '100%'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '2rem 2rem 1rem 2rem',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '2rem'
                }}>
                  🗑️
                </div>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#dc2626'
                }}>
                  Delete Teacher
                </h3>
                <p style={{
                  margin: '0 0 1rem 0',
                  color: '#6b7280',
                  lineHeight: 1.6
                }}>
                  Are you sure you want to delete <strong style={{ color: '#374151' }}>{selectedTeacher.name}</strong>?
                </p>
                <p style={{
                  margin: 0,
                  color: '#dc2626',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}>
                  This action cannot be undone and will permanently remove all teacher data.
                </p>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1rem 2rem 2rem 2rem',
                display: 'flex',
                justifyContent: 'center',
                gap: '1rem'
              }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedTeacher(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: submitting
                      ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                      : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Deleting...
                    </>
                  ) : (
                    '🗑️ Delete Teacher'
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </Layout>
  );
};

export default AdminTeacherManagement;